import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import {
  MAX_OPTIONS,
  MAX_INVITEES,
  MAX_TITLE_LEN,
  hostEventSchema,
  type CreateEvent as CreateEventInput,
  type HostEvent,
} from '../../shared/protocol';
import { OptionPicker, type DraftOption } from './OptionPicker';
import { apiV2, type Friend, type FriendGroup, type SendInvite } from '../lib/apiV2';
import { authFetch, isSignedIn, onSessionChange } from '../lib/auth';
import {
  clearDraft,
  emptyDraft,
  loadDraft,
  saveDraft,
  type CreateDraft,
} from '../lib/draft';
import { formatWhen, eventLink, hostLink, inviteShortLink, copyToClipboard } from '../lib/share';
import { AuthSheet } from './AuthSheet';
import { FriendForm, emptyFriendDraft } from './FriendsManager';
import { GuidesRow } from './Guides';
import { ShareRow } from './ShareRow';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const STEP_LABELS = ['What & when', 'Who’s coming', 'Send it'] as const;

/** POST /api/events with the bearer attached when signed in (legacy anonymous
 * create keeps working — the token is simply absent). */
async function createEventAuthed(body: CreateEventInput): Promise<HostEvent> {
  const res = await authFetch('/api/events', { method: 'POST', body: JSON.stringify(body) });
  const raw: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = (raw as { message?: string } | null)?.message;
    throw new ApiError('CREATE', msg ?? `Could not create the night (${res.status}).`, res.status);
  }
  return hostEventSchema.parse(raw);
}

function sendBadge(p: { email?: string | null; phone?: string | null; carrierGateway?: string | null }): string {
  const mail = Boolean(p.email);
  const text = Boolean(p.phone && p.carrierGateway);
  if (mail && text) return '✉+📱';
  if (mail) return '✉';
  if (text) return '📱';
  return '—';
}

/**
 * The 3-screen create stepper — replaces the old 4-card monolith. Screen 1:
 * what & when (movie hero + pills). Screen 2: who's coming (favorite friends
 * pre-checked). Screen 3: send it (summary + server-side invite sends).
 * Draft autosaves to localStorage so the magic-link round trip loses nothing.
 */
export function CreateEvent({
  onCreated,
  onExit,
  initialStep,
}: {
  onCreated: (ev: HostEvent) => void;
  onExit: () => void;
  initialStep?: 1 | 2 | 3;
}) {
  const [draft, setDraft] = useState<CreateDraft>(() => {
    const d = loadDraft() ?? emptyDraft();
    if (initialStep) d.step = initialStep;
    return d;
  });
  const [signedIn, setSignedIn] = useState(isSignedIn());
  const [friends, setFriends] = useState<Friend[] | null>(null);
  const [moreMovies, setMoreMovies] = useState(draft.options.length > 1);
  const [moreOptions, setMoreOptions] = useState(false);
  const [editWhen, setEditWhen] = useState(false);
  const [editRoom, setEditRoom] = useState(false);
  const [addingFriend, setAddingFriend] = useState(false);
  const [friendBusy, setFriendBusy] = useState(false);
  const [authSheet, setAuthSheet] = useState(false);
  const [submitting, setSubmitting] = useState<'send' | 'silent' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{
    ev: HostEvent;
    sent: number;
    /** every channel failed for these — nothing was delivered (THU-185) */
    failed: number;
    failureReasons: string[];
    skipped: number;
    sends: boolean;
    /** invites "sent" but via Resend's fallback sender — account inbox only */
    warned: boolean;
  } | null>(null);
  const prefsApplied = useRef(false);
  /** Host's saved Discord webhook. The DO only posts to Discord when the EVENT
   * carries a webhook (eventRoom.ts), so a webhook saved in Settings does
   * nothing unless the create payload carries it forward. */
  const [discordWebhook, setDiscordWebhook] = useState<string | null>(null);
  const [groups, setGroups] = useState<FriendGroup[]>([]);

  const patch = useCallback((p: Partial<CreateDraft>) => {
    setDraft((prev) => ({ ...prev, ...p }));
  }, []);

  // autosave the draft on every change (until the night is created)
  useEffect(() => {
    if (!done) saveDraft(draft);
  }, [draft, done]);

  useEffect(() => onSessionChange(() => setSignedIn(isSignedIn())), []);

  // entering screen 3 unauthenticated slides the auth sheet up over the review
  useEffect(() => {
    if (draft.step === 3 && !signedIn) setAuthSheet(true);
  }, [draft.step, signedIn]);

  // signed in → load saved friends + default room code from prefs
  useEffect(() => {
    if (!signedIn) {
      setFriends(null);
      return;
    }
    let alive = true;
    apiV2
      .listFriends()
      .then((list) => {
        if (!alive) return;
        list.sort((a, b) => Number(b.favorite) - Number(a.favorite) || a.name.localeCompare(b.name));
        setFriends(list);
      })
      .catch(() => alive && setFriends([]));
    apiV2
      .listGroups()
      .then((g) => alive && setGroups(g))
      .catch(() => undefined); // groups are a shortcut — never block the flow
    apiV2
      .getPrefs()
      .then((p) => {
        if (!alive) return;
        setDiscordWebhook(p.discordWebhook ?? null);
        if (prefsApplied.current) return;
        prefsApplied.current = true;
        setDraft((prev) =>
          prev.roomCode === emptyDraft().roomCode ? { ...prev, roomCode: p.defaultRoomCode } : prev,
        );
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [signedIn]);

  // favorites pre-checked, exactly once
  useEffect(() => {
    if (!friends || draft.selectionInitialized) return;
    const favs = friends.filter((f) => f.favorite).map((f) => f.id);
    patch({ selectedFriendIds: favs, selectionInitialized: true });
  }, [friends, draft.selectionInitialized, patch]);

  // ---- derived ----
  const movieTitle = draft.options[0]?.title.trim() ?? '';
  const effectiveTitle = (
    draft.titleEdited && draft.title.trim()
      ? draft.title.trim()
      : movieTitle
        ? `Thursday Night: ${movieTitle}`
        : 'Thursday Night'
  ).slice(0, MAX_TITLE_LEN);
  const cleanOptions = draft.options
    .map((o) => ({ ...o, title: o.title.trim() }))
    .filter((o) => o.title.length > 0);
  const canLeaveS1 = cleanOptions.length > 0 || (draft.titleEdited && draft.title.trim().length > 0);

  const selectedFriends = useMemo(
    () => (friends ?? []).filter((f) => draft.selectedFriendIds.includes(f.id)),
    [friends, draft.selectedFriendIds],
  );
  const invitedCount = selectedFriends.length + draft.pendingFriends.length;

  const setOption = (i: number, v: DraftOption) =>
    patch({ options: draft.options.map((o, idx) => (idx === i ? v : o)) });
  const addOption = () => {
    if (draft.options.length < MAX_OPTIONS) patch({ options: [...draft.options, { title: '' }] });
  };
  const removeOption = (i: number) => {
    if (i === 0) return;
    patch({ options: draft.options.filter((_, idx) => idx !== i) });
  };

  /** Is every member of this group already picked? Drives the chip's on state
   * so a second tap clears the group rather than being a no-op. */
  const groupIsOn = (g: FriendGroup) =>
    g.memberIds.length > 0 && g.memberIds.every((id) => draft.selectedFriendIds.includes(id));

  /** One tap = the whole group selected, on top of whatever's already picked.
   * Inline "new friend" adds occupy invite slots too, so the ceiling accounts
   * for them; an oversized group fills what room is left instead of failing. */
  const toggleGroup = (g: FriendGroup) => {
    if (groupIsOn(g)) {
      patch({ selectedFriendIds: draft.selectedFriendIds.filter((id) => !g.memberIds.includes(id)) });
      return;
    }
    const room = MAX_INVITEES - draft.pendingFriends.length;
    const merged = [...draft.selectedFriendIds];
    for (const id of g.memberIds) {
      if (merged.length >= room) break;
      if (!merged.includes(id)) merged.push(id);
    }
    patch({ selectedFriendIds: merged });
  };

  const toggleFriend = (id: string) => {
    const on = draft.selectedFriendIds.includes(id);
    if (!on && invitedCount >= MAX_INVITEES) return;
    patch({
      selectedFriendIds: on
        ? draft.selectedFriendIds.filter((x) => x !== id)
        : [...draft.selectedFriendIds, id],
    });
  };

  const goto = (step: 1 | 2 | 3) => {
    setError(null);
    patch({ step });
    window.scrollTo({ top: 0 });
  };

  // ---- the submit pipeline ----
  const submit = async (sendInvites: boolean) => {
    setError(null);
    const startsAt = new Date(draft.startsLocal);
    if (Number.isNaN(startsAt.getTime())) {
      setError('Pick a valid date and time.');
      return;
    }
    if (cleanOptions.length === 0 && !draft.titleEdited) {
      setError('Pick a movie first (or set a title under More options).');
      return;
    }
    if (!draft.roomCode.trim()) {
      setError('Set the room code so people can join.');
      return;
    }

    // send-pipeline picks: saved friends ride by id (the worker snapshots their
    // contact info server-side); inline adds carry their contact details
    const picks: Array<SendInvite & { name: string }> = [
      ...selectedFriends.map((f) => ({
        friendId: f.id,
        name: f.name,
        email: f.email ?? undefined,
        phone: f.phone ?? undefined,
        carrierGateway: f.carrierGateway ?? undefined,
        attendMode: f.attendMode,
      })),
      ...draft.pendingFriends.map((p) => ({
        name: p.name,
        email: p.email || undefined,
        phone: p.phone || undefined,
        carrierGateway: p.carrierGateway ?? undefined,
        attendMode: p.attendMode,
      })),
    ];
    // the DO keys invitees by display name — dedupe to keep the mapping clean
    const seen = new Set<string>();
    const invitees = picks.filter((p) => {
      const k = p.name.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    const body: CreateEventInput = {
      v: 1,
      title: effectiveTitle,
      startsAt: startsAt.toISOString(),
      place: draft.roomCode.trim(),
      recurrence: {
        freq: draft.freq,
        ...(draft.freq === 'weekly' || draft.freq === 'biweekly' ? { weekday: draft.weekday } : {}),
      },
      note: draft.note.trim() || undefined,
      options: cleanOptions.length
        ? cleanOptions.map((o) => ({
            title: o.title,
            year: o.year,
            posterUrl: o.posterUrl,
            tmdbId: o.tmdbId,
            overview: o.overview,
            hint: o.hint,
          }))
        : [{ title: effectiveTitle }],
      invitees: invitees.slice(0, MAX_INVITEES).map((p) => p.name),
      ...(discordWebhook ? { discordWebhook } : {}),
    };

    setSubmitting(sendInvites ? 'send' : 'silent');
    try {
      // inline-added friends become permanent friends first (best-effort)
      if (signedIn && draft.pendingFriends.length > 0) {
        for (const p of draft.pendingFriends) {
          try {
            await apiV2.addFriend(p);
          } catch {
            // non-fatal — the night still goes out with the snapshot below
          }
        }
      }

      const ev = await createEventAuthed(body);

      let sent = 0;
      let failed = 0;
      let failureReasons: string[] = [];
      let skipped = 0;
      let warned = false;
      if (sendInvites && invitees.length > 0) {
        // the worker matches these to the DO-minted voter codes and mails/texts
        // each person their /i/ short link
        const r = await apiV2.sendInvites(ev.id, ev.hostToken, invitees);
        sent = r.sent;
        failed = r.failed;
        failureReasons = r.failureReasons;
        skipped = r.skipped;
        warned = r.fallbackWarning;
      }

      clearDraft();
      setDone({ ev, sent, failed, failureReasons, skipped, sends: sendInvites, warned });
      // reflect the host link in the URL so a refresh keeps the dashboard
      const u = new URL(window.location.href);
      u.search = '';
      u.searchParams.set('e', ev.id);
      u.searchParams.set('host', ev.hostToken);
      window.history.replaceState({}, '', u.toString());
      window.scrollTo({ top: 0 });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the night.');
    } finally {
      setSubmitting(null);
    }
  };

  // ---------- success state (still "screen 3" — dashboard is one tap away) ----------
  if (done) {
    const { ev, sent, failed, failureReasons, skipped, sends, warned } = done;
    // THU-185: `failed` outranks everything else in the headline. Telling a host
    // "Invites are out." while nothing was delivered is the one outcome here
    // that costs them the night, and it is what this screen used to do.
    const nothingLanded = sends && sent === 0 && failed > 0;
    return (
      <div className="stepper-page">
        <div className="toast" role="status">
          {!sends
            ? 'Night created — no invites sent.'
            : nothingLanded
              ? 'Night created — but the invites did NOT go out.'
              : failed > 0
                ? `Night created — ${sent} invite${sent === 1 ? '' : 's'} out, ${failed} could not be delivered.`
                : warned
                  ? 'Invites went out — with a delivery warning.'
                  : 'Invites are out.'}
        </div>
        <div className="card marquee bulbs">
          <h2 className="display">It’s on the marquee</h2>
          <p className="soft">
            <strong>{ev.title}</strong> · {formatWhen(ev.startsAt)} · room{' '}
            <span className="mono gold">{ev.place}</span>
          </p>
          {sends && warned ? (
            // fallback-sender sends are NOT delivered to friends — no clean check here
            <div className="callout-amber">
              <span className="callout-icon" aria-hidden="true">
                ⚠
              </span>
              <div>
                delivered to the account inbox only — verify thursday-night.com in Resend for
                friend delivery
                {skipped > 0 ? ` · ${skipped} skipped (no contact info)` : ''}
              </div>
            </div>
          ) : sends && failed > 0 ? (
            // Reasons come from the worker already phrased for a person, each
            // ending in something the host can actually do next. The personal
            // invite links are rendered further down this very screen, which is
            // what "send their link yourself" refers to.
            <div className="callout-amber">
              <span className="callout-icon" aria-hidden="true">
                ⚠
              </span>
              <div>
                {sent > 0
                  ? `${sent} invite${sent === 1 ? '' : 's'} went out. ${failed} did not:`
                  : `None of the ${failed} invite${failed === 1 ? '' : 's'} went out:`}
                <ul className="reason-list">
                  {failureReasons.map((why) => (
                    <li key={why}>{why}</li>
                  ))}
                </ul>
                {skipped > 0 ? `${skipped} skipped (no contact info). ` : ''}
                Their personal invite links are below — use those and the night still works.
              </div>
            </div>
          ) : sends ? (
            <p className="soft">
              {sent} invite{sent === 1 ? '' : 's'} sent
              {skipped > 0 ? ` · ${skipped} skipped (no contact info)` : ''} — texts are best-effort,
              email always goes out.
            </p>
          ) : (
            <p className="soft">Share the links below yourself, or send invites from the dashboard.</p>
          )}
          <button className="btn primary block" onClick={() => onCreated(ev)}>
            Go to your dashboard →
          </button>
        </div>

        {ev.invitees.length > 0 && (
          <div className="card">
            <h3>Personal invite links</h3>
            <p className="soft">Each one is that friend’s RSVP + vote page — the same link the texts carry.</p>
            <div className="friend-list">
              {ev.invitees.map((inv) => (
                <ShortLinkRow key={inv.code} name={inv.name} link={inviteShortLink(inv.code)} />
              ))}
            </div>
          </div>
        )}

        <div className="card">
          <h3>Share the night</h3>
          <ShareRow
            link={eventLink(ev.id)}
            parts={{
              title: ev.title,
              startsAt: ev.startsAt,
              place: ev.place,
              recurrence: ev.recurrence,
              note: ev.note,
              link: eventLink(ev.id),
            }}
          />
        </div>

        <h3 className="section-heading display">How everyone joins</h3>
        <GuidesRow roomCode={ev.place} />
        <p className="hint-line center">
          Your private host link:{' '}
          <button className="btn link" onClick={() => copyToClipboard(hostLink(ev.id, ev.hostToken))}>
            copy it
          </button>{' '}
          — it’s the only way back to the dashboard.
        </p>
      </div>
    );
  }

  // ---------- stepper chrome ----------
  const header = (
    <div className="stepper-head">
      <button
        className="back-btn"
        onClick={() => (draft.step === 1 ? onExit() : goto((draft.step - 1) as 1 | 2))}
        aria-label={draft.step === 1 ? 'Back to home' : 'Back a step'}
      >
        ←
      </button>
      <div className="stepper-dots" aria-hidden="true">
        {[1, 2, 3].map((s) => (
          <span key={s} className="dot-wrap">
            <span
              className={`dot${s < draft.step ? ' done' : ''}${s === draft.step ? ' active' : ''}`}
            />
            {s < 3 && <span className="dot-line" />}
          </span>
        ))}
      </div>
      <span className="stepper-count">{draft.step} of 3</span>
      <div className="stepper-labels">
        {STEP_LABELS.map((l, i) => (
          <span key={l} className={i + 1 === draft.step ? 'on' : ''}>
            {l}
          </span>
        ))}
      </div>
    </div>
  );

  // ---------- screen 1: what & when ----------
  if (draft.step === 1) {
    return (
      <div className="stepper-page">
        {header}
        <h2 className="display screen-title">What &amp; when</h2>
        <p className="soft screen-sub">A movie and a time. The rest is optional.</p>

        <div className="card marquee">
          <OptionPicker
            index={0}
            value={draft.options[0] ?? { title: '' }}
            onChange={(v) => setOption(0, v)}
            onRemove={() => setOption(0, { title: '' })}
            canRemove={false}
            label="The movie"
          />
          {!moreMovies ? (
            <button className="btn link" onClick={() => setMoreMovies(true)}>
              + Add more movies — let everyone vote
            </button>
          ) : (
            <>
              {draft.options.slice(1).map((o, i) => (
                <OptionPicker
                  key={i + 1}
                  index={i + 1}
                  value={o}
                  onChange={(v) => setOption(i + 1, v)}
                  onRemove={() => removeOption(i + 1)}
                  canRemove
                />
              ))}
              {draft.options.length < MAX_OPTIONS && (
                <button className="btn link" onClick={addOption}>
                  + Add another ({draft.options.length}/{MAX_OPTIONS})
                </button>
              )}
            </>
          )}
        </div>

        <div className="card">
          <div className="pill-stack">
            <button className="pill-btn" onClick={() => setEditWhen((v) => !v)}>
              <span className="pill-k">When</span>
              <span className="pill-v">{formatWhen(draft.startsLocal)}</span>
              <span className="pill-hint">tap to change</span>
            </button>
            {editWhen && (
              <input
                type="datetime-local"
                value={draft.startsLocal}
                onChange={(e) => patch({ startsLocal: e.target.value })}
              />
            )}
            <button className="pill-btn" onClick={() => setEditRoom((v) => !v)}>
              <span className="pill-k">Room</span>
              <span className="pill-v mono gold">{draft.roomCode || '—'}</span>
              <span className="pill-hint">tap to change</span>
            </button>
            {editRoom && (
              <input
                type="text"
                className="mono"
                value={draft.roomCode}
                maxLength={20}
                onChange={(e) => patch({ roomCode: e.target.value.toUpperCase() })}
              />
            )}
          </div>

          <details
            className="disclosure"
            open={moreOptions}
            onToggle={(e) => setMoreOptions((e.target as HTMLDetailsElement).open)}
          >
            <summary>More options</summary>
            <label>Title</label>
            <input
              type="text"
              value={draft.titleEdited ? draft.title : effectiveTitle}
              maxLength={MAX_TITLE_LEN}
              onChange={(e) => patch({ title: e.target.value, titleEdited: true })}
            />
            <label>
              Note <span className="hint">(optional)</span>
            </label>
            <textarea
              placeholder="Pizza at 7:30, doors open 7:45"
              value={draft.note}
              maxLength={500}
              onChange={(e) => patch({ note: e.target.value })}
            />
            <div className="row">
              <div>
                <label>Repeats</label>
                <select
                  value={draft.freq}
                  onChange={(e) => patch({ freq: e.target.value as CreateDraft['freq'] })}
                >
                  <option value="none">One time only</option>
                  <option value="weekly">Every week</option>
                  <option value="biweekly">Every other week</option>
                  <option value="monthly">Every month</option>
                </select>
              </div>
              {(draft.freq === 'weekly' || draft.freq === 'biweekly') && (
                <div>
                  <label>On</label>
                  <select
                    value={draft.weekday}
                    onChange={(e) => patch({ weekday: Number(e.target.value) })}
                  >
                    {WEEKDAYS.map((d, i) => (
                      <option key={d} value={i}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </details>
        </div>

        {error && <div className="notice error">{error}</div>}
        <button className="btn primary block cta" onClick={() => goto(2)} disabled={!canLeaveS1}>
          Next: who’s coming →
        </button>
      </div>
    );
  }

  // ---------- screen 2: who's coming ----------
  if (draft.step === 2) {
    return (
      <div className="stepper-page">
        {header}
        <h2 className="display screen-title">Who’s coming</h2>
        <p className="soft screen-sub">
          {signedIn ? 'Favorites are already checked.' : 'Add tonight’s crew — sign-in comes at the last step.'}
        </p>

        <div className="card marquee">
          {!signedIn && (
            <div className="notice info">
              Your saved friends appear here once you’re signed in — anyone you add now is saved for
              next time too.
            </div>
          )}
          {signedIn && friends === null && (
            <div className="center">
              <span className="spinner" /> Loading your friends…
            </div>
          )}
          {signedIn && friends !== null && friends.length === 0 && draft.pendingFriends.length === 0 && (
            <div className="empty-state">
              No friends saved yet. Add them once — invites go out automatically every night after.
            </div>
          )}

          {groups.length > 0 && (
            <div className="group-row">
              <p className="soft">Groups — one tap invites everyone in them.</p>
              <div className="chip-grid">
                {groups.map((g) => (
                  <button
                    key={g.id}
                    className={`chip toggle${groupIsOn(g) ? ' on' : ''}`}
                    onClick={() => toggleGroup(g)}
                    aria-pressed={groupIsOn(g)}
                  >
                    {groupIsOn(g) && <span className="tick">✓ </span>}
                    {g.name}
                    <span className="hint"> ({g.memberIds.length})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="chip-grid big">
            {(friends ?? []).map((f) => {
              const on = draft.selectedFriendIds.includes(f.id);
              return (
                <button
                  key={f.id}
                  className={`chip toggle big${on ? ' on' : ''}`}
                  onClick={() => toggleFriend(f.id)}
                  aria-pressed={on}
                >
                  {on && <span className="tick">✓ </span>}
                  {f.favorite && <span className="fav">★ </span>}
                  {f.name}
                </button>
              );
            })}
            {draft.pendingFriends.map((p, i) => (
              <button
                key={`new-${i}`}
                className="chip toggle big on"
                onClick={() =>
                  patch({ pendingFriends: draft.pendingFriends.filter((_, idx) => idx !== i) })
                }
                title="New friend — tap to remove"
              >
                <span className="tick">✓ </span>
                {p.name} <span className="new-tag">new</span>
              </button>
            ))}
            <button className="chip toggle add" onClick={() => setAddingFriend(true)}>
              + New friend
            </button>
          </div>

          <p className="count-line">
            {invitedCount === 0 ? 'Nobody yet — you can still share links later.' : `${invitedCount} invited`}
            {invitedCount >= MAX_INVITEES ? ` (max ${MAX_INVITEES})` : ''}
          </p>
        </div>

        {error && <div className="notice error">{error}</div>}
        <button className="btn primary block cta" onClick={() => goto(3)}>
          Review →
        </button>

        {addingFriend && (
          <div className="sheet-backdrop" role="dialog" aria-modal="true">
            <div className="sheet">
              <div className="card marquee">
                <h2 className="display">New friend</h2>
                <FriendForm
                  initial={emptyFriendDraft()}
                  submitLabel="Add to the night"
                  busy={friendBusy}
                  onCancel={() => setAddingFriend(false)}
                  onSubmit={async (fd) => {
                    if (invitedCount >= MAX_INVITEES) {
                      setAddingFriend(false);
                      return;
                    }
                    if (signedIn) {
                      setFriendBusy(true);
                      try {
                        await apiV2.addFriend(fd);
                        const list = await apiV2.listFriends();
                        list.sort(
                          (a, b) =>
                            Number(b.favorite) - Number(a.favorite) || a.name.localeCompare(b.name),
                        );
                        setFriends(list);
                        const added = list.find(
                          (f) => f.name.toLowerCase() === fd.name.toLowerCase(),
                        );
                        if (added) toggleFriend(added.id);
                        setAddingFriend(false);
                      } catch {
                        // fall back to the pending path so the night isn't blocked
                        patch({ pendingFriends: [...draft.pendingFriends, fd] });
                        setAddingFriend(false);
                      } finally {
                        setFriendBusy(false);
                      }
                    } else {
                      patch({ pendingFriends: [...draft.pendingFriends, fd] });
                      setAddingFriend(false);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- screen 3: send it ----------
  const poster = cleanOptions.find((o) => o.posterUrl)?.posterUrl;
  const allPicks = [
    ...selectedFriends.map((f) => ({
      key: f.id,
      name: f.name,
      email: f.email,
      phone: f.phone,
      carrierGateway: f.carrierGateway,
    })),
    ...draft.pendingFriends.map((p, i) => ({
      key: `new-${i}`,
      name: p.name,
      email: p.email ?? null,
      phone: p.phone ?? null,
      carrierGateway: p.carrierGateway ?? null,
    })),
  ];
  const contactless = allPicks.filter((p) => sendBadge(p) === '—');

  return (
    <div className="stepper-page">
      {header}
      <h2 className="display screen-title">Send it</h2>

      <div className="card marquee bulbs summary-card">
        <div className="summary-main">
          <div
            className="summary-poster"
            style={poster ? { backgroundImage: `url(${poster})` } : undefined}
            aria-hidden="true"
          >
            {!poster && '🎬'}
          </div>
          <div className="summary-facts">
            <h3 className="display">{effectiveTitle}</h3>
            <div className="fact">{formatWhen(draft.startsLocal)}</div>
            <div className="fact">
              Room <span className="mono gold">{draft.roomCode}</span>
            </div>
            {cleanOptions.length > 1 && (
              <div className="fact soft">{cleanOptions.length} movies on the ballot — everyone votes</div>
            )}
            {draft.note.trim() && <div className="fact soft">“{draft.note.trim()}”</div>}
          </div>
        </div>

        <div className="summary-invitees">
          {allPicks.length === 0 ? (
            <p className="soft">No invitees — you’ll share the link yourself from the dashboard.</p>
          ) : (
            <div className="chip-grid">
              {allPicks.map((p) => (
                <span className="chip" key={p.key}>
                  {p.name} <span className="badge-mini">{sendBadge(p)}</span>
                </span>
              ))}
            </div>
          )}
          {contactless.length > 0 && (
            <p className="hint-line">
              {contactless.map((p) => p.name).join(', ')}: no contact info — share the link yourself.
            </p>
          )}
        </div>

        {error && <div className="notice error">{error}</div>}

        <button
          className="btn primary block cta"
          onClick={() => (signedIn ? submit(true) : setAuthSheet(true))}
          disabled={submitting !== null}
        >
          {submitting === 'send' ? (
            <>
              <span className="spinner" /> Rolling out the marquee…
            </>
          ) : signedIn ? (
            'Send invites'
          ) : (
            'Sign in & send invites'
          )}
        </button>
        <button
          className="btn link block"
          onClick={() => submit(false)}
          disabled={submitting !== null}
        >
          {submitting === 'silent' ? 'Creating…' : 'Create without sending'}
        </button>
      </div>

      {authSheet && !signedIn && !submitting && (
        <AuthSheet overlay title="One last thing" onClose={() => setAuthSheet(false)} />
      )}
    </div>
  );
}

function ShortLinkRow({ name, link }: { name: string; link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="friend-row">
      <div className="friend-info">
        <div className="name">{name}</div>
        <div className="sub mono">{link}</div>
      </div>
      <button
        className="btn secondary small"
        onClick={async () => {
          await copyToClipboard(link);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}
